import React, { useRef, useState } from "react";

import * as d3 from "d3";
import { useEffect } from "react";

const HorizontalBarChart = ({
    datasets,
    id,
    DataValueTypeId,
    SetChartToolTip,
    SetChartToolTipKeyboard,
    ClearLocationToolTip,
    barColor,
    stratificationID,
}) => {
    useEffect(() => {
        changeData();
    }, [stratificationID]);


    const chartRef = useRef(null);

    const [stateIndex, setStateIndex] = useState(0);
    const [isFirst, setIsFirst] = useState(true);
    function ShowHorizontalBarChart(DataItems) {
        const currentChart = document.querySelector(`svg#${id}`);
        const workingDataItems = DataItems;
        const width = 500;
        const height = 2000;
        const margin = 30;

        if (currentChart !== null) currentChart.remove();

        const svg = d3
            .select("#" + id)
            .append("svg")
            .attr("id", id)
            .attr("viewBox", `-50 0 ${width + 2 * margin} ${height + 2.5 * margin}`)
            .attr("width", "100%")
            .attr("height", height)
            .attr("preserveAspectRatio", "xMidYMin");

        const xScale = d3.scaleLinear().range([0, width]);
        const yScale = d3.scaleBand().range([0, height]).padding(0.4);

        let g = svg
            .append("g")
            .attr("class", "horizontal-chart-content")
            .attr("transform", "translate(" + 0 + "," + margin + ")")
            .on("mouseout", function (mouseEvent) {
                if (mouseEvent.toElement == null) return;
                if (mouseEvent.toElement.id === id) {
                    ClearLocationToolTip("clear");
                }
            });

        yScale.domain(workingDataItems.map((d) => d.LocationDesc));

        xScale.domain([
            0,
            d3.max(workingDataItems, (d) =>
                d.HighConfidenceLimit === null
                    ? d.DataValue + d.DataValue / 20
                    : d.HighConfidenceLimit + d.DataValue / 20
            ),
        ]);

        const halfBarHeight = yScale.bandwidth() / 2;
        const halfTickLength = 3;

        const USValueLine = workingDataItems.filter(
            (d) => d.LocationAbbr === "US"
        )[0].DataValue;

        // X-Axis Bottom
        g.append("g")
            .attr("class", "xAxis")
            .attr("transform", "translate(0," + height + ")")
            .attr("font-family", "Nunito")
            .call(d3.axisBottom(xScale).ticks(5));

        // X-Axis Top
        g.append("g")
            .attr("class", "xAxis")
            .attr("font-family", "Nunito")
            .attr("transform", "translate(0, 0)")
            .call(d3.axisTop(xScale).ticks(5));

        // Y-Axis Left
        g.append("g")
            .call(d3.axisLeft(yScale))
            .attr("id", "yLabel")
            .attr("font-family", "Nunito")
            .attr("font-size", "13px");

        let yTicks = Array.from(d3.selectAll("#yLabel .tick text"));
        yTicks.map(
            (tickLabel, index) =>
                (tickLabel.textContent = `${workingDataItems[index].DataValueFootnoteSymbol} ${tickLabel.textContent}`)
        );

        // Value Bars
        g.selectAll("rect")
            .data(workingDataItems)
            .enter()
            .append("rect")
            .attr("class", (d, i) => "graph-text " + (i === 10 ? "bar breakPage" : "bar"))
            .attr("stratid", (d) => d.StratificationID)
            .attr("locid", (d) => d.LocationAbbr)
            .attr("aria-label", function (d, i) {
                return getAriaTextForMapLocation(d, i, d.LocationAbbr);
            })
            .attr("x", 0)
            .attr("y", (d) => yScale(d.LocationDesc))
            .attr("fill", barColor)
            .attr("width", (d) => xScale(d.DataValue && d.DataValue !== null ? d.DataValue : .01))
            .attr("height", yScale.bandwidth())
            .on("mouseover", function (mouseEvent, locationInfo) {
                SetChartToolTip(mouseEvent, locationInfo);
            });

        // Confidence Limit's Horizontal Line
        g.selectAll("bar")
            .data(workingDataItems)
            .enter()
            .append("line")

            .attr("x1", (d) => {
                if (d.HighConfidenceLimit === null) return 0;
                return xScale(d.LowConfidenceLimit);
            })
            .attr("x2", (d) => {
                if (d.HighConfidenceLimit === null) return 0;
                return xScale(d.HighConfidenceLimit);
            })
            .attr("y1", (d) => {
                return yScale(d.LocationDesc) + halfBarHeight;
            })
            .attr("y2", (d) => {
                return yScale(d.LocationDesc) + halfBarHeight;
            })
            .attr("stroke", "black")
            .attr("stroke-width", 1);

        // Confidence Limit's Left Vertical Line
        g.selectAll("bar")
            .data(workingDataItems)
            .enter()
            .append("line")

            .attr("x1", (d) => {
                if (d.HighConfidenceLimit === null) return 0;
                return xScale(d.LowConfidenceLimit);
            })
            .attr("x2", (d) => {
                if (d.HighConfidenceLimit === null) return 0;
                return xScale(d.LowConfidenceLimit);
            })
            .attr(
                "y1",
                (d) => yScale(d.LocationDesc) + halfBarHeight - halfTickLength
            )
            .attr(
                "y2",
                (d) => yScale(d.LocationDesc) + halfBarHeight + halfTickLength
            )
            .attr("stroke", "black")
            .attr("stroke-width", 1);

        // Confidence Limit's Right Vertical Line
        g.selectAll("bar")
            .data(workingDataItems)
            .enter()
            .append("line")

            .attr("x1", (d) => {
                if (d.HighConfidenceLimit === null) return 0;
                return xScale(d.HighConfidenceLimit);
            })
            .attr("x2", (d) => {
                if (d.HighConfidenceLimit === null) return 0;
                return xScale(d.HighConfidenceLimit);
            })
            .attr(
                "y1",
                (d) => yScale(d.LocationDesc) + halfBarHeight - halfTickLength
            )
            .attr(
                "y2",
                (d) => yScale(d.LocationDesc) + halfBarHeight + halfTickLength
            )
            .attr("stroke", "black")
            .attr("stroke-width", 1);

        g.append("line")
            .attr("class", "USValueLine")
            .attr("x1", xScale(USValueLine))
            .attr("x2", xScale(USValueLine))
            .attr("y1", margin)
            .attr("y2", height + margin)
            .attr("stroke", "gray")
            .attr("stroke-width", 1);
    }

    let i = 0;
    function changeData() {
        ShowHorizontalBarChart(datasets.DataItems);
    }

    function handleKeyDown(event) {
        var itemList = chartRef.current?.querySelectorAll(".graph-text");
        var tickList = chartRef.current?.querySelectorAll(".tick");
        if (event.code === "Tab" && event.shiftKey) {
            //nothing. let's just catch it
        }
        else if (event.code === "End") {
            event.preventDefault();
            let nextIndex = null;
            if (isFirst) {
                nextIndex = 0;
                setIsFirst(false);
            } else {
                nextIndex = itemList.length - 1;
            }


            itemList.forEach((item, i) => {
                item.setAttribute("aria-selected", "false");
                item.classList.remove("graph-focus");
            });

            itemList[nextIndex].classList.add("graph-focus");
            itemList[nextIndex].focus();

            itemList[nextIndex].setAttribute("aria-selected", "true");
            setStateIndex(nextIndex);

            scrollIntoViewIfNotVisible(itemList[nextIndex]);

        }
        else if (event.code === "Home") {
            event.preventDefault();
            let nextIndex = null;
            if (isFirst) {
                nextIndex = 0;
                setIsFirst(false);
            } else {
                nextIndex = 0;
            }


            itemList.forEach((item, i) => {
                item.setAttribute("aria-selected", "false");
                item.classList.remove("graph-focus");
            });

            itemList[nextIndex].classList.add("graph-focus");
            itemList[nextIndex].focus();

            itemList[nextIndex].setAttribute("aria-selected", "true");
            setStateIndex(nextIndex);

            scrollIntoViewIfNotVisible(itemList[nextIndex]);

        }
        else if (event.code !== "Tab") {
            event.preventDefault();
            if (event.code === "ArrowDown") {
                console.log("arrow down");

                let nextIndex = null;
                if (isFirst) {
                    nextIndex = 0;
                    setIsFirst(false);
                } else {
                    nextIndex = stateIndex + 1;
                }

                if (nextIndex < itemList.length) {
                    itemList.forEach((item, i) => {
                        item.setAttribute("aria-selected", "false");
                        item.classList.remove("graph-focus");
                    });

                    itemList[nextIndex].classList.add("graph-focus");
                    itemList[nextIndex].focus();

                    itemList[nextIndex].setAttribute("aria-selected", "true");
                    setStateIndex(nextIndex);

                    scrollIntoViewIfNotVisible(itemList[nextIndex]);
                }
            } else if (event.code === "ArrowUp") {
                console.log("arrow up");

                let nextIndex = null;
                if (isFirst) {
                    nextIndex = 0;
                    setIsFirst(false);
                } else {
                    nextIndex = stateIndex - 1;
                }
                if (nextIndex > -1) {
                    itemList.forEach((item, i) => {
                        item.setAttribute("aria-selected", "false");
                        item.classList.remove("graph-focus");
                    });
                    itemList[nextIndex].setAttribute("aria-selected", "true");
                    itemList[nextIndex].classList.add("graph-focus");
                    itemList[nextIndex].focus();

                    setStateIndex(nextIndex);

                    scrollIntoViewIfNotVisible(itemList[nextIndex]);
                }
            } else if (event.code === "Enter") {
                const stratid = itemList[stateIndex].getAttribute("stratid");
                const locid = itemList[stateIndex].getAttribute("locid");
                const y = itemList[stateIndex].getAttribute("y");
                const x = itemList[stateIndex].getAttribute("x");

                const items = datasets.DataItems.filter(x => x.LocationAbbr === locid && x.StratificationID === stratid);
                if (items && items.length > 0) {
                    SetChartToolTipKeyboard(x, y, event, itemList[stateIndex], items[0])
                }
            }
        }
    }


    function scrollIntoViewIfNotVisible(target) {
        if (target.getBoundingClientRect().bottom > window.innerHeight) {
            target.scrollIntoView(false);
        }

        if (target.getBoundingClientRect().top < 0) {
            target.scrollIntoView();
        }
    }

    function handleBlur(event) {
        var itemList = chartRef.current?.querySelectorAll(".graph-text");

        itemList.forEach((item, i) => {
            item.classList.remove("graph-focus");
        });

    }
    function getAriaTextForMapLocation(d, i, locationCode) {
        const locations = datasets.DataItems.filter((x) => x.LocationAbbr === locationCode);
        const locationText = locations && locations.length > 0 ? locations[0].LocationDesc : null;

        if (locationText !== null) {
            const stratid = d.StratificationID;
            const locid = d.LocationAbbr;
            const items = datasets.DataItems.filter(x => x.LocationAbbr === locid && x.StratificationID === stratid);

            if (d && d.DataValue !== null) {
                return `Select to see data for ${d.Stratification}, ${d.LocationDesc}`;
            }
            else {
                return `Data not available for ${d.Stratification}, ${d.LocationDesc}`;
            }
        }
        else {

            return "Error creating text"
        }
    }
    return (<div
        onKeyDown={handleKeyDown}
        tabIndex="0"
        onBlur={handleBlur}
        role="listbox"
        aria-label="Horizontal Bar Graph"        
        aria-keyshortcuts="ArrowUp,ArrowDown,Home,End"
    >
        <div id={id} ref={chartRef} className="horizontal-chart order-2"></div>
    </div>);
};

export default HorizontalBarChart;
